import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { FormCurrencyInput, FormTextInput } from '@divvy-web/skylab.form'
import { useForm } from '@divvy-web/skylab.react-hook-form'

import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import { useGetCreditApplicationForReview } from '@sputnik/pages/CreditApplicationPages/ReviewForm/GetCreditApplicationForReview.gql'
import SectionCollapsibleList from '@sputnik/pages/CreditApplicationPages/ReviewForm/SectionCollapsibleList'
import {
  hasEmptyValue,
  hasMissingRequiredField,
  toSentenceCase,
} from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import styles from './styles.module.scss'

const FinancialInfoReviewSection = () => {
  const navigate = useNavigate()
  const { appId } = useParams()
  const { control } = useForm()
  const { isMigration, isMajorAccountChange } = useGetCreditAppTypes({ salesforceCreditId: appId ?? '' })
  const [getClassName] = useNameFormatter('FinancialInfoReviewSection')
  const { tracker } = useWebTracking()

  const { data } = useGetCreditApplicationForReview({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const { billingCycle, businessInfo, financeInfo } = data?.creditApplication || {}
  const { annualRevenue, accountingSoftware } = businessInfo || {}
  const { desiredCredit, expectedSpend } = financeInfo || {}

  const hasMissingInfo =
    !!data &&
    hasMissingRequiredField([
      annualRevenue,
      expectedSpend,
      desiredCredit,
      billingCycle,
      accountingSoftware?.accountingSoftwareValue,
    ])

  const handleOnEditClick = () => {
    tracker?.track('se_signup__review_and_sign_edit_info__fired', {
      application_section: 'Finance Info',
      navigated_to_page: `${PATHNAME_CONSTS.FINANCIAL_DETAILS_PATH}`,
    })
    navigate(`../${PATHNAME_CONSTS.FINANCIAL_DETAILS_PATH}`)
  }

  if (isMigration || isMajorAccountChange) {
    return null
  }

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <SectionCollapsibleList
        hasMissingInfo={hasMissingInfo}
        headerText={
          <FormattedMessage
            defaultMessage='Financial info'
            id='sputnik.FinancialInfoReviewSection__YVlBeJ'
          />
        }
        onEditClick={handleOnEditClick}
      >
        <FormCurrencyInput
          readOnly
          control={control}
          isRequired={hasEmptyValue(annualRevenue)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Annual revenue'
                id='sputnik.FinancialInfoReviewSection__16D5H3'
              />
            </span>
          }
          name='annualRevenue'
          value={annualRevenue === null ? 0 : annualRevenue}
        />

        <FormCurrencyInput
          readOnly
          control={control}
          isRequired={hasEmptyValue(expectedSpend)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Expected monthly spend'
                id='sputnik.FinancialInfoReviewSection__dsX8S3'
              />
            </span>
          }
          name='expectedSpend'
          value={expectedSpend === null ? 0 : expectedSpend}
        />
        <FormCurrencyInput
          readOnly
          control={control}
          isRequired={hasEmptyValue(desiredCredit)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Desired line of credit'
                id='sputnik.FinancialInfoReviewSection__bUcdiS'
              />
            </span>
          }
          name='desiredCredit'
          value={desiredCredit === null ? 0 : desiredCredit}
        />
        <FormTextInput
          readOnly
          control={control}
          isRequired={hasEmptyValue(billingCycle)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Billing cycle'
                id='sputnik.FinancialInfoReviewSection__Z7xDUp'
              />
            </span>
          }
          name='billingCycle'
          value={billingCycle ? toSentenceCase(billingCycle) : '—'}
        />
        <FormTextInput
          readOnly
          control={control}
          isRequired={hasEmptyValue(accountingSoftware?.accountingSoftwareValue)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Accounting software'
                id='sputnik.FinancialInfoReviewSection__WAi7JV'
              />
            </span>
          }
          name='accountingSoftware'
          value={accountingSoftware?.accountingSoftwareValue ? accountingSoftware?.accountingSoftwareValue : '—'}
        />
      </SectionCollapsibleList>
    </div>
  )
}

export default FinancialInfoReviewSection
